import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./PublicHeader.css";

type PublicHeaderProps = {
  variant?: "transparent" | "solid";
  rightLinks?: Array<{ label: string; to: string }>;
};

const DEFAULT_LINKS = [
  { label: "Home", to: "/public" },
  { label: "Find a Family Doctor", to: "/public/find-family-doctor" },
  { label: "How It Works", to: "/public/how-it-works" },
  { label: "About", to: "/public/about" },
  { label: "For Providers", to: "/public/for-providers" },
  { label: "Contact", to: "/public/contact" },
];

// SVG Icons
const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

function useIsMobile(breakpointPx = 900) {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [breakpointPx]);

  return isMobile;
}

function useScrolled(threshold = 10) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({
  variant = "transparent",
  rightLinks = DEFAULT_LINKS,
}) => {
  const location = useLocation();
  const currentPath = location?.pathname || "";
  const isMobile = useIsMobile(900);
  const scrolled = useScrolled(10);

  const links = useMemo(() => rightLinks, [rightLinks]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [currentPath]);

  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const headerClass = [
    "ocf-ph",
    `ocf-ph--${variant}`,
    scrolled ? "ocf-ph--scrolled" : "",
  ].filter(Boolean).join(" ");

  return (
    <>
    <header className={headerClass}>
      <div className="ocf-ph-inner">
        <Link to="/public" className="ocf-ph-brand" aria-label="AcceptingMD Home">
          <div className="ocf-ph-logoFrame" aria-hidden>
            <img src="/logo-1.png" alt="" />
          </div>
          <div className="ocf-ph-brandName">AcceptingMD</div>
        </Link>

        {/* Desktop nav */}
        {!isMobile && (
          <nav className="ocf-ph-nav" aria-label="Primary">
            {links.map((l) => {
              const isActive =
                currentPath === l.to ||
                (l.to !== "/public" && currentPath.startsWith(l.to));
              const isCta = l.label === "Find a Family Doctor";

              const className = [
                "ocf-ph-link",
                isActive ? "ocf-ph-link--active" : "",
                isCta ? "ocf-ph-link--cta" : "",
              ].filter(Boolean).join(" ");

              return (
                <Link
                  key={`${l.label}-${l.to}`}
                  to={l.to}
                  className={className}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span className="ocf-ph-link-text">{l.label}</span>
                </Link>
              );
            })}
          </nav>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <button
            type="button"
            className="ocf-ph-menuBtn"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        )}
      </div>

      {/* Mobile menu with animation */}
      {isMobile && (
        <div className={`ocf-ph-menuWrap ${menuOpen ? "ocf-ph-menuWrap--open" : ""}`}>
          <nav className="ocf-ph-menu" aria-label="Mobile Primary">
            {links.map((l, idx) => {
              const isActive =
                currentPath === l.to ||
                (l.to !== "/public" && currentPath.startsWith(l.to));
              const isCta = l.label === "Find a Family Doctor";

              const className = [
                "ocf-ph-menuLink",
                isActive ? "ocf-ph-menuLink--active" : "",
                isCta ? "ocf-ph-menuLink--cta" : "",
              ].filter(Boolean).join(" ");

              return (
                <Link
                  key={`m-${l.label}-${l.to}`}
                  to={l.to}
                  className={className}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  onClick={() => setMenuOpen(false)}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Overlay */}
      {isMobile && menuOpen && (
        <div className="ocf-ph-overlay" onClick={() => setMenuOpen(false)} />
      )}
    </header>
    {/* Spacer to prevent content from hiding under fixed header */}
    <div className="ocf-ph-spacer" />
    </>
  );
};

export default PublicHeader;
